// @ts-nocheck
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { CompanyContextValue } from './CompanyContextTypes';
import { useNavigate } from 'react-router-dom';
import {
  getAnalyses,
  getAnalysis,
  createAnalysis as createAnalysisApi,
  copyAnalysis as copyAnalysisApi,
  updateAnalysis as updateAnalysisApi,
  updateAnalysisData as updateAnalysisDataApi,
  updateAnalysisIndividuals as updateAnalysisIndividualsApi,
  updateAnalysisCompensations as updateAnalysisCompensationsApi,
  updateAnalysisBaseAmounts as updateAnalysisBaseAmountsApi,
  updateAnalysisSeverances as updateAnalysisSeverancesApi,
  updateAnalysisParachuteJsonSection as updateAnalysisParachuteJsonSectionApi,
  PARACHUTE_JSON_SECTION_ORDER,
  PARACHUTE_JSON_SECTION_DATA_KEY,
  deleteAnalysis as deleteAnalysisApi,
  getArchivedAnalyses as getArchivedAnalysesApi,
  restoreAnalysis as restoreAnalysisApi,
} from '../../../services/AnalysisApiClient';
import { isAnalysisLocked } from '../../../constants/analysisStatus';
import { isServerBackedAnalysisId } from '../../../utils/isServerBackedAnalysisId';
import { DEFAULT_PARACHUTE_EQUITY_SUB_ID, DEFAULT_PARACHUTE_WORKFLOW_SUB_ID } from '../parachuteWorkflowSubItems';
import { DEFAULT_DOCUMENTS_WORKFLOW_SUB_ID } from '../documentsWorkflowSubItems';

/** Normalize individuals from API or legacy local state for signatures and PUT payloads. */
function normalizeIndividualsFromData(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((i) => {
      const cat = String(i.category ?? 'hci');
      const category = cat === 'officer' || cat === 'shareholder' || cat === 'hci' ? cat : 'hci';
      return {
        id: String(i.id ?? ''),
        name: String(i.name ?? ''),
        email: String(i.email ?? ''),
        title: String(i.title ?? ''),
        category,
      };
    })
    .filter((i) => i.id.length > 0);
}

/** Canonical headcount string for forms and API (camelCase `headcount`; tolerate PascalCase / legacy keys). */
function pickHeadcount(d) {
  if (d == null || typeof d !== 'object') return '';
  const v = d.headcount ?? d.Headcount ?? d.highestHeadCount;
  return v == null || v === '' ? '' : String(v);
}

function individualsSignature(d) {
  const rows = normalizeIndividualsFromData(d?.individuals);
  const sorted = [...rows].sort((a, b) => a.id.localeCompare(b.id));
  const headcount = pickHeadcount(d);
  return `${JSON.stringify(sorted)}|${headcount}`;
}

function compensationSignature(d) {
  const rows = normalizeIndividualsFromData(d?.individuals);
  const sorted = [...rows].sort((a, b) => a.id.localeCompare(b.id));
  const comp = d?.compensation && typeof d.compensation === 'object' ? d.compensation : {};
  const parts = sorted.map((i) => {
    const c = comp[i.id] || {};
    return [
      i.id,
      String(c.startDate ?? ''),
      String(c.baseSalary ?? ''),
      String(c.bonusPct ?? ''),
      String(c.healthPremium ?? ''),
    ].join('\t');
  });
  return parts.join('|');
}

function baseAmountSignature(d) {
  const bad = d?.baseAmountData && typeof d.baseAmountData === 'object' ? d.baseAmountData : {};
  const ids = Object.keys(bad).sort();
  return ids
    .map((id) => {
      const row = bad[id];
      if (row == null || typeof row !== 'object' || Array.isArray(row)) {
        return `${id}:`;
      }
      const keys = Object.keys(row).sort();
      return `${id}:${keys.map((k) => `${k}=${String(row[k] ?? '')}`).join(';')}`;
    })
    .join('|');
}

function severanceSignature(d) {
  const rows = normalizeIndividualsFromData(d?.individuals);
  const sorted = [...rows].sort((a, b) => a.id.localeCompare(b.id));
  const sev = d?.severanceData && typeof d.severanceData === 'object' ? d.severanceData : {};
  const parts = sorted.map((i) => {
    const s = sev[i.id] || {};
    return [
      i.id,
      String(s.salaryAmount ?? ''),
      String(s.salaryUnit ?? ''),
      String(s.bonusPortion ?? ''),
      String(s.cobraMonths ?? ''),
      String(s.other ?? ''),
      String(s.paymentTiming ?? ''),
    ].join('\t');
  });
  return parts.join('|');
}

function stringifyRecordForParachuteSig(rec) {
  if (!rec || typeof rec !== 'object' || Array.isArray(rec)) return '';
  const keys = Object.keys(rec).sort();
  return keys.map((k) => `${k}:${JSON.stringify(rec[k])}`).join(';');
}

/** One parachute JSON section (CIC, equity, …) — used for granular persist. */
function parachuteSectionSignature(d, section) {
  const dataKey = PARACHUTE_JSON_SECTION_DATA_KEY[section];
  return stringifyRecordForParachuteSig(d?.[dataKey]);
}

function buildParachuteSectionSigMap(d) {
  const map = {};
  for (const section of PARACHUTE_JSON_SECTION_ORDER) {
    map[section] = parachuteSectionSignature(d, section);
  }
  return map;
}

/** Shallow copy of parachute JSON maps from client data for merge-after-save. */
function buildParachuteDataOverlay(d) {
  const overlay = {};
  for (const section of PARACHUTE_JSON_SECTION_ORDER) {
    const dataKey = PARACHUTE_JSON_SECTION_DATA_KEY[section];
    if (d[dataKey] != null && typeof d[dataKey] === 'object' && !Array.isArray(d[dataKey])) {
      overlay[dataKey] = { ...d[dataKey] };
    }
  }
  return overlay;
}

/** Default analysis data – keys match sidebar: Transaction, Individuals, Compensation, Base Amount, Parachute Payments, 280G Results, Shareholder Approval. See AnalysisData in AnalysisApiClient. */
export const emptyAnalysis = {
  companyName: '',
  closingDate: '',
  companyWorkStatus: '',
  dealStructure: '',
  dealRole: '',
  listingStatus: '',
  dealPrice: '',
  afr: '5',
  headcount: '',
  individuals: [],
  compensation: {},
  baseAmountData: {},
  severanceData: {},
  cicBonusData: {},
  compIncreaseData: {},
  equityData: {},
  buyerEquityData: {},
  otherPaymentsData: {},
};

/** Merge defaults and normalize headcount so Individuals step always receives `data.headcount`. */
function normalizeAnalysisData(raw) {
  if (raw == null || typeof raw !== 'object') return { ...emptyAnalysis };
  const headcount = pickHeadcount(raw);
  const merged = { ...emptyAnalysis, ...raw, headcount };
  const legacy = merged.currentComp;
  let compensation = merged.compensation;
  if (
    (!compensation || Object.keys(compensation).length === 0) &&
    legacy &&
    typeof legacy === 'object' &&
    Object.keys(legacy).length > 0
  ) {
    compensation = legacy;
  }
  const { currentComp: _drop, ...rest } = merged;
  return { ...rest, compensation: compensation || {}, headcount };
}

function mapAnalysesFromApi(list) {
  if (!Array.isArray(list)) return [];
  return list.map((a) => ({ ...a, data: normalizeAnalysisData(a.data) }));
}

const CLIENT_PATH_TO_TAB = {
  '/client': 'home',
  '/client/': 'home',
  '/client/analyses': 'analyses',
  '/client/archives': 'archives',
  '/client/settings': 'settings',
  '/client/transaction': 'transaction',
  '/client/individuals': 'individuals',
  '/client/compensation': 'compensation',
  '/client/base-amount': 'baseAmount',
  '/client/parachute': 'parachute',
  '/client/analysis': 'analysis',
  '/client/documents': 'documents',
};

const CLIENT_TAB_TO_PATH = {
  home: '/client',
  analyses: '/client/analyses',
  archives: '/client/archives',
  settings: '/client/settings',
  transaction: '/client/transaction',
  individuals: '/client/individuals',
  compensation: '/client/compensation',
  baseAmount: '/client/base-amount',
  parachute: '/client/parachute',
  analysis: '/client/analysis',
  documents: '/client/documents',
};

const CompanyContext = createContext<CompanyContextValue | null>(null);

export function CompanyProvider({ children }) {
  const navigate = useNavigate();
  const seededRef = useRef(false);
  const [analyses, setAnalyses] = useState([]);
  const [currentAnalysisId, setCurrentAnalysisId] = useState(null);
  const [parachuteSubTab, setParachuteSubTab] = useState(DEFAULT_PARACHUTE_WORKFLOW_SUB_ID);
  const [parachuteEquitySubTab, setParachuteEquitySubTab] = useState(DEFAULT_PARACHUTE_EQUITY_SUB_ID);
  const [documentsSubTab, setDocumentsSubTab] = useState(DEFAULT_DOCUMENTS_WORKFLOW_SUB_ID);
  const [archivedAnalyses, setArchivedAnalyses] = useState([]);
  /** Bumped after `refreshAnalysesList` so listeners (e.g. workspace company) can refetch. */
  const [analysesRefreshVersion, setAnalysesRefreshVersion] = useState(0);
  /** First `getAnalyses()` finished — lets `/client/analyses` show a skeleton instead of a wrong empty state. */
  const [analysesListReady, setAnalysesListReady] = useState(false);
  const analysesRef = useRef(analyses);
  analysesRef.current = analyses;

  /** Drop legacy client-side analysis cache; server is the source of truth. */
  useEffect(() => {
    try {
      localStorage.removeItem('parachute-analyses');
      localStorage.removeItem('parachute-current-id');
      localStorage.removeItem('parachute-archived');
    } catch (_) {}
  }, []);

  const effectiveCurrentId = currentAnalysisId ?? analyses[0]?.id;
  const currentAnalysis = analyses.find((a) => a.id === effectiveCurrentId) || analyses[0];
  const isCurrentAnalysisLocked = useMemo(() => isAnalysisLocked(currentAnalysis?.status), [currentAnalysis?.status]);
  const data = useMemo(() => normalizeAnalysisData(currentAnalysis?.data), [currentAnalysis]);
  const lastSavedBasicsRef = useRef<Record<string, string>>({});
  const lastSavedIndividualsRef = useRef<Record<string, string>>({});
  const lastSavedCompensationRef = useRef<Record<string, string>>({});
  const lastSavedBaseAmountRef = useRef<Record<string, string>>({});
  const lastSavedSeveranceRef = useRef<Record<string, string>>({});
  /** Per analysis id → per parachute section signature (only dirty sections are PUT). */
  const lastSavedParachuteSectionsRef = useRef<Record<string, Record<string, string>>>({});
  /** One fetch per analysis id when company-linked but resolved name/date not yet in state. */
  const companyHydrateAttemptedRef = useRef(new Set());

  /** Dirty detection for transaction PUT: deal fields + company link ids (name/closing come from the company table on the server). */
  const basicsSignature = (d, analysisRow) => {
    const dealPrice = String(d?.dealPrice ?? '');
    const afr = String(d?.afr ?? '5');
    const headcount = pickHeadcount(d);
    const lf = String(analysisRow?.lawFirmId ?? '');
    const cid = String(analysisRow?.companyId ?? '');
    return [dealPrice, afr, headcount, lf, cid].join('|');
  };

  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;

    getAnalyses()
      .then((list) => {
        if (list.length > 0) {
          setAnalyses(mapAnalysesFromApi(list));
          setCurrentAnalysisId((prev) => (prev && list.some((x) => x.id === prev) ? prev : list[0].id));
        } else {
          setAnalyses((prev) => {
            const next = prev.length > 0 ? prev.map((a) => ({ ...a, data: normalizeAnalysisData(a.data) })) : [];
            setCurrentAnalysisId((cid) => {
              if (next.length === 0) return null;
              if (cid && next.some((a) => a.id === cid)) return cid;
              return next[0].id;
            });
            return next;
          });
        }
      })
      .catch(() => {
        setAnalyses((prev) => {
          const next = prev.length > 0 ? prev.map((a) => ({ ...a, data: normalizeAnalysisData(a.data) })) : [];
          setCurrentAnalysisId((cid) => {
            if (next.length === 0) return null;
            if (cid && next.some((a) => a.id === cid)) return cid;
            return next[0].id;
          });
          return next;
        });
      })
      .finally(() => setAnalysesListReady(true));
  }, []);

  useEffect(() => {
    if (currentAnalysisId == null && analyses.length > 0) {
      setCurrentAnalysisId(analyses[0].id);
    }
  }, [currentAnalysisId, analyses]);

  /** Per-analysis workflow: reset nested step picks when switching analyses (same idea as `navigateTo` defaults). */
  useEffect(() => {
    if (currentAnalysisId == null) return;
    setParachuteSubTab(DEFAULT_PARACHUTE_WORKFLOW_SUB_ID);
    setParachuteEquitySubTab(DEFAULT_PARACHUTE_EQUITY_SUB_ID);
    setDocumentsSubTab(DEFAULT_DOCUMENTS_WORKFLOW_SUB_ID);
  }, [currentAnalysisId]);

  /** Reload analyses from the server for the current firm/company scope (same list as initial load). */
  const refreshAnalysesList = useCallback(async () => {
    try {
      const list = await getAnalyses();
      if (list.length > 0) {
        const mapped = mapAnalysesFromApi(list);
        setAnalyses(mapped);
        setCurrentAnalysisId((prev) => {
          const ids = mapped.map((a) => a.id);
          if (prev && ids.includes(prev)) return prev;
          return mapped[0].id;
        });
      } else {
        setAnalyses([]);
        setCurrentAnalysisId(null);
      }
      setAnalysesRefreshVersion((v) => v + 1);
    } catch {
      /* keep existing state */
    }
  }, []);

  /** Linked analyses: pull resolved company name + closing date from the list API if missing locally. */
  useEffect(() => {
    if (!effectiveCurrentId || !isServerBackedAnalysisId(effectiveCurrentId)) return;
    const row = analysesRef.current.find((a) => a.id === effectiveCurrentId);
    if (!row?.lawFirmId || !row?.companyId) return;
    const nd = normalizeAnalysisData(row.data);
    const hasName = String(nd.companyName || '').trim().length > 0;
    const hasDate = String(nd.closingDate || '').trim().length > 0;
    if (hasName && hasDate) return;
    if (companyHydrateAttemptedRef.current.has(effectiveCurrentId)) return;
    companyHydrateAttemptedRef.current.add(effectiveCurrentId);

    let cancelled = false;
    getAnalyses()
      .then((list) => {
        if (cancelled) return;
        setAnalyses(mapAnalysesFromApi(list));
      })
      .catch(() => {
        companyHydrateAttemptedRef.current.delete(effectiveCurrentId);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveCurrentId]);

  /** `getAnalyses()` is list-only (no workflow steps). `getAnalysis(id)` loads all steps—required for the active analysis. */
  useEffect(() => {
    if (!effectiveCurrentId || !isServerBackedAnalysisId(effectiveCurrentId)) return;
    const idTarget = effectiveCurrentId;
    let cancelled = false;

    getAnalysis(idTarget)
      .then((full) => {
        if (cancelled) return;
        if (String(full.id) !== String(idTarget)) return;
        const normalized = normalizeAnalysisData(full.data);
        const row = { ...full, data: normalized };
        setAnalyses((prev) =>
          prev.map((a) => {
            if (a.id !== idTarget) return a;
            return {
              ...row,
              status: row.status ?? a.status,
              workflowProgress: row.workflowProgress ?? a.workflowProgress,
            };
          })
        );
        lastSavedBasicsRef.current[idTarget] = basicsSignature(normalized, row);
        lastSavedIndividualsRef.current[idTarget] = individualsSignature(normalized);
        lastSavedCompensationRef.current[idTarget] = compensationSignature(normalized);
        lastSavedBaseAmountRef.current[idTarget] = baseAmountSignature(normalized);
        lastSavedSeveranceRef.current[idTarget] = severanceSignature(normalized);
        lastSavedParachuteSectionsRef.current[idTarget] = buildParachuteSectionSigMap(normalized);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [effectiveCurrentId, analysesRefreshVersion]);

  // Initialize "last saved" snapshot for the current analysis id.
  useEffect(() => {
    if (!effectiveCurrentId) return;
    // Use key existence check instead of truthiness so an "empty" signature ('') still counts as initialized.
    if (Object.prototype.hasOwnProperty.call(lastSavedBasicsRef.current, effectiveCurrentId)) return;
    lastSavedBasicsRef.current[effectiveCurrentId] = basicsSignature(data, currentAnalysis);
  }, [effectiveCurrentId]);

  useEffect(() => {
    if (!effectiveCurrentId) return;
    if (Object.prototype.hasOwnProperty.call(lastSavedIndividualsRef.current, effectiveCurrentId)) return;
    lastSavedIndividualsRef.current[effectiveCurrentId] = individualsSignature(data);
  }, [effectiveCurrentId]);

  useEffect(() => {
    if (!effectiveCurrentId) return;
    if (Object.prototype.hasOwnProperty.call(lastSavedCompensationRef.current, effectiveCurrentId)) return;
    lastSavedCompensationRef.current[effectiveCurrentId] = compensationSignature(data);
  }, [effectiveCurrentId]);

  useEffect(() => {
    if (!effectiveCurrentId) return;
    if (Object.prototype.hasOwnProperty.call(lastSavedBaseAmountRef.current, effectiveCurrentId)) return;
    lastSavedBaseAmountRef.current[effectiveCurrentId] = baseAmountSignature(data);
  }, [effectiveCurrentId]);

  useEffect(() => {
    if (!effectiveCurrentId) return;
    if (Object.prototype.hasOwnProperty.call(lastSavedSeveranceRef.current, effectiveCurrentId)) return;
    lastSavedSeveranceRef.current[effectiveCurrentId] = severanceSignature(data);
  }, [effectiveCurrentId]);

  useEffect(() => {
    if (!effectiveCurrentId) return;
    if (Object.prototype.hasOwnProperty.call(lastSavedParachuteSectionsRef.current, effectiveCurrentId)) return;
    lastSavedParachuteSectionsRef.current[effectiveCurrentId] = buildParachuteSectionSigMap(data);
  }, [effectiveCurrentId]);

  const persistBasicsIfChanged = useCallback(
    async (overrideData) => {
      if (!effectiveCurrentId) return;
      const row = analyses.find((a) => a.id === effectiveCurrentId);
      if (row && isAnalysisLocked(row.status)) return;
      const d = overrideData ?? data;
      const currentSig = basicsSignature(d, row);
      const lastSigExists = Object.prototype.hasOwnProperty.call(lastSavedBasicsRef.current, effectiveCurrentId);
      const lastSig = lastSavedBasicsRef.current[effectiveCurrentId] ?? '';

      if (!lastSigExists || currentSig !== lastSig) {
        const payload = {
          dealPrice: String(d.dealPrice ?? ''),
          afr: String(d.afr ?? '5'),
          headcount: pickHeadcount(d),
        };
        if (row?.lawFirmId && row?.companyId) {
          payload.lawFirmId = row.lawFirmId;
          payload.companyId = row.companyId;
        }

        const updated = await updateAnalysisDataApi(effectiveCurrentId, payload);

        if (updated) {
          setAnalyses((prev) =>
            prev.map((a) => {
              if (a.id !== effectiveCurrentId) return a;
              const mergedData = updated.data ? normalizeAnalysisData({ ...a.data, ...updated.data }) : a.data;
              return {
                ...a,
                name: updated.name ?? a.name,
                status: updated.status ?? a.status,
                lawFirmId: updated.lawFirmId ?? a.lawFirmId,
                companyId: updated.companyId ?? a.companyId,
                workflowLastChangedAt: updated.workflowLastChangedAt ?? a.workflowLastChangedAt,
                lastRunAt: updated.lastRunAt ?? a.lastRunAt,
                voteRoomLaunchedAt: updated.voteRoomLaunchedAt ?? a.voteRoomLaunchedAt,
                data: mergedData,
              };
            })
          );
        }

        const nextRow = {
          lawFirmId: updated?.lawFirmId ?? row?.lawFirmId,
          companyId: updated?.companyId ?? row?.companyId,
        };
        lastSavedBasicsRef.current[effectiveCurrentId] = basicsSignature(d, nextRow);
      }
    },
    [effectiveCurrentId, data, analyses]
  );

  const persistIndividualsIfChanged = useCallback(
    async (overrideData) => {
      if (!effectiveCurrentId) return;
      const analysisRow = analyses.find((a) => a.id === effectiveCurrentId);
      if (analysisRow && isAnalysisLocked(analysisRow.status)) return;
      const d = overrideData ?? data;
      const rows = normalizeIndividualsFromData(d.individuals);
      const currentSig = individualsSignature(d);
      const lastSigExists = Object.prototype.hasOwnProperty.call(lastSavedIndividualsRef.current, effectiveCurrentId);
      const lastSig = lastSavedIndividualsRef.current[effectiveCurrentId] ?? '';

      if (!lastSigExists || currentSig !== lastSig) {
        const updated = await updateAnalysisIndividualsApi(effectiveCurrentId, rows, pickHeadcount(d));
        if (updated == null) {
          throw new Error('Analysis not found');
        }
        if (updated?.data) {
          const fromServer = updated.data.individuals ? normalizeIndividualsFromData(updated.data.individuals) : rows;
          const headcountFromServer = pickHeadcount(updated.data) || pickHeadcount(d);
          setAnalyses((prev) =>
            prev.map((a) =>
              a.id === effectiveCurrentId
                ? {
                    ...a,
                    status: updated.status ?? a.status,
                    workflowLastChangedAt: updated.workflowLastChangedAt ?? a.workflowLastChangedAt,
                    lastRunAt: updated.lastRunAt ?? a.lastRunAt,
                    voteRoomLaunchedAt: updated.voteRoomLaunchedAt ?? a.voteRoomLaunchedAt,
                    data: { ...a.data, individuals: fromServer, headcount: headcountFromServer },
                  }
                : a
            )
          );
          lastSavedBasicsRef.current[effectiveCurrentId] = basicsSignature(
            { ...d, headcount: headcountFromServer },
            analysisRow
          );
        }
        lastSavedIndividualsRef.current[effectiveCurrentId] = currentSig;
      }
    },
    [effectiveCurrentId, data, analyses]
  );

  const persistCompensationIfChanged = useCallback(
    async (overrideData) => {
      if (!effectiveCurrentId) return;
      const row = analyses.find((a) => a.id === effectiveCurrentId);
      if (row && isAnalysisLocked(row.status)) return;
      const d = overrideData ?? data;
      const rows = normalizeIndividualsFromData(d.individuals);
      const currentSig = compensationSignature(d);
      const lastSigExists = Object.prototype.hasOwnProperty.call(lastSavedCompensationRef.current, effectiveCurrentId);
      const lastSig = lastSavedCompensationRef.current[effectiveCurrentId] ?? '';

      if (!lastSigExists || currentSig !== lastSig) {
        const comp = d.compensation && typeof d.compensation === 'object' ? d.compensation : {};
        const updated = await updateAnalysisCompensationsApi(effectiveCurrentId, rows, comp);
        if (updated == null) {
          throw new Error('Analysis not found');
        }
        if (updated?.data?.compensation != null && typeof updated.data.compensation === 'object') {
          setAnalyses((prev) =>
            prev.map((a) =>
              a.id === effectiveCurrentId
                ? {
                    ...a,
                    status: updated.status ?? a.status,
                    workflowLastChangedAt: updated.workflowLastChangedAt ?? a.workflowLastChangedAt,
                    lastRunAt: updated.lastRunAt ?? a.lastRunAt,
                    voteRoomLaunchedAt: updated.voteRoomLaunchedAt ?? a.voteRoomLaunchedAt,
                    data: { ...a.data, compensation: { ...updated.data.compensation } },
                  }
                : a
            )
          );
        }
        lastSavedCompensationRef.current[effectiveCurrentId] = currentSig;
      }
    },
    [effectiveCurrentId, data, analyses]
  );

  const persistBaseAmountIfChanged = useCallback(
    async (overrideData) => {
      if (!effectiveCurrentId) return;
      const row = analyses.find((a) => a.id === effectiveCurrentId);
      if (row && isAnalysisLocked(row.status)) return;
      const d = overrideData ?? data;
      const rows = normalizeIndividualsFromData(d.individuals);
      const currentSig = baseAmountSignature(d);
      const lastSigExists = Object.prototype.hasOwnProperty.call(lastSavedBaseAmountRef.current, effectiveCurrentId);
      const lastSig = lastSavedBaseAmountRef.current[effectiveCurrentId] ?? '';

      if (!lastSigExists || currentSig !== lastSig) {
        const bad = d.baseAmountData && typeof d.baseAmountData === 'object' ? d.baseAmountData : {};
        const updated = await updateAnalysisBaseAmountsApi(effectiveCurrentId, rows, bad);
        if (updated == null) {
          throw new Error('Analysis not found');
        }
        if (updated?.data?.baseAmountData != null && typeof updated.data.baseAmountData === 'object') {
          setAnalyses((prev) =>
            prev.map((a) =>
              a.id === effectiveCurrentId
                ? {
                    ...a,
                    status: updated.status ?? a.status,
                    workflowLastChangedAt: updated.workflowLastChangedAt ?? a.workflowLastChangedAt,
                    lastRunAt: updated.lastRunAt ?? a.lastRunAt,
                    voteRoomLaunchedAt: updated.voteRoomLaunchedAt ?? a.voteRoomLaunchedAt,
                    data: { ...a.data, baseAmountData: { ...updated.data.baseAmountData } },
                  }
                : a
            )
          );
        }
        lastSavedBaseAmountRef.current[effectiveCurrentId] = currentSig;
      }
    },
    [effectiveCurrentId, data, analyses]
  );

  const persistSeveranceIfChanged = useCallback(
    async (overrideData) => {
      if (!effectiveCurrentId) return;
      const row = analyses.find((a) => a.id === effectiveCurrentId);
      if (row && isAnalysisLocked(row.status)) return;
      const d = overrideData ?? data;
      const rows = normalizeIndividualsFromData(d.individuals);
      const currentSig = severanceSignature(d);
      const lastSigExists = Object.prototype.hasOwnProperty.call(lastSavedSeveranceRef.current, effectiveCurrentId);
      const lastSig = lastSavedSeveranceRef.current[effectiveCurrentId] ?? '';

      if (!lastSigExists || currentSig !== lastSig) {
        const sev = d.severanceData && typeof d.severanceData === 'object' ? d.severanceData : {};
        const updated = await updateAnalysisSeverancesApi(effectiveCurrentId, rows, sev);
        if (updated == null) {
          throw new Error('Analysis not found');
        }
        if (updated?.data?.severanceData != null && typeof updated.data.severanceData === 'object') {
          setAnalyses((prev) =>
            prev.map((a) =>
              a.id === effectiveCurrentId
                ? {
                    ...a,
                    status: updated.status ?? a.status,
                    workflowLastChangedAt: updated.workflowLastChangedAt ?? a.workflowLastChangedAt,
                    lastRunAt: updated.lastRunAt ?? a.lastRunAt,
                    voteRoomLaunchedAt: updated.voteRoomLaunchedAt ?? a.voteRoomLaunchedAt,
                    data: { ...a.data, severanceData: { ...updated.data.severanceData } },
                  }
                : a
            )
          );
        }
        lastSavedSeveranceRef.current[effectiveCurrentId] = currentSig;
      }
    },
    [effectiveCurrentId, data, analyses]
  );

  const persistParachuteJsonSectionsIfChanged = useCallback(
    async (overrideData) => {
      if (!effectiveCurrentId) return;
      const row = analyses.find((a) => a.id === effectiveCurrentId);
      if (row && isAnalysisLocked(row.status)) return;
      const d = overrideData ?? data;
      let prevMap = lastSavedParachuteSectionsRef.current[effectiveCurrentId];
      if (!prevMap) {
        prevMap = buildParachuteSectionSigMap(d);
        lastSavedParachuteSectionsRef.current[effectiveCurrentId] = prevMap;
      }

      const dirtySections = PARACHUTE_JSON_SECTION_ORDER.filter(
        (section) => parachuteSectionSignature(d, section) !== (prevMap[section] ?? '')
      );
      if (dirtySections.length === 0) return;

      if (!isServerBackedAnalysisId(effectiveCurrentId)) {
        lastSavedParachuteSectionsRef.current[effectiveCurrentId] = buildParachuteSectionSigMap(d);
        return;
      }

      const rows = normalizeIndividualsFromData(d.individuals);
      const nextMap = { ...prevMap };
      try {
        let last = null;
        for (const section of dirtySections) {
          const dataKey = PARACHUTE_JSON_SECTION_DATA_KEY[section];
          const rec =
            d[dataKey] != null && typeof d[dataKey] === 'object' && !Array.isArray(d[dataKey]) ? d[dataKey] : {};
          last = await updateAnalysisParachuteJsonSectionApi(effectiveCurrentId, section, rows, rec);
          if (last == null) {
            console.warn('Parachute persist: analysis not found or inaccessible', effectiveCurrentId);
            return;
          }
          nextMap[section] = parachuteSectionSignature(d, section);
          lastSavedParachuteSectionsRef.current[effectiveCurrentId] = { ...nextMap };
        }
        if (last?.data) {
          const fromServer = normalizeAnalysisData(last.data);
          const overlay = buildParachuteDataOverlay(d);
          setAnalyses((prev) =>
            prev.map((a) =>
              a.id === effectiveCurrentId
                ? {
                    ...a,
                    status: last.status ?? a.status,
                    workflowLastChangedAt: last.workflowLastChangedAt ?? a.workflowLastChangedAt,
                    lastRunAt: last.lastRunAt ?? a.lastRunAt,
                    voteRoomLaunchedAt: last.voteRoomLaunchedAt ?? a.voteRoomLaunchedAt,
                    data: normalizeAnalysisData({ ...fromServer, ...overlay }),
                  }
                : a
            )
          );
        }
      } catch (e) {
        console.error('Parachute JSON sections could not be saved (navigation still allowed):', e);
      }
    },
    [effectiveCurrentId, data, analyses]
  );

  const hasUnsavedWorkflowChanges = useCallback(
    (overrideData) => {
      if (!effectiveCurrentId) return false;
      const row = analyses.find((a) => a.id === effectiveCurrentId);
      if (row && isAnalysisLocked(row.status)) return false;
      const d = overrideData ?? data;

      const basicsSig = basicsSignature(d, row);
      const lastBasicsExists = Object.prototype.hasOwnProperty.call(lastSavedBasicsRef.current, effectiveCurrentId);
      if (!lastBasicsExists || basicsSig !== lastSavedBasicsRef.current[effectiveCurrentId]) return true;

      const indSig = individualsSignature(d);
      const lastIndExists = Object.prototype.hasOwnProperty.call(lastSavedIndividualsRef.current, effectiveCurrentId);
      if (!lastIndExists || indSig !== lastSavedIndividualsRef.current[effectiveCurrentId]) return true;

      const compSig = compensationSignature(d);
      const lastCompExists = Object.prototype.hasOwnProperty.call(lastSavedCompensationRef.current, effectiveCurrentId);
      if (!lastCompExists || compSig !== lastSavedCompensationRef.current[effectiveCurrentId]) return true;

      const baseSig = baseAmountSignature(d);
      const lastBaseExists = Object.prototype.hasOwnProperty.call(lastSavedBaseAmountRef.current, effectiveCurrentId);
      if (!lastBaseExists || baseSig !== lastSavedBaseAmountRef.current[effectiveCurrentId]) return true;

      const sevSig = severanceSignature(d);
      const lastSevExists = Object.prototype.hasOwnProperty.call(lastSavedSeveranceRef.current, effectiveCurrentId);
      if (!lastSevExists || sevSig !== lastSavedSeveranceRef.current[effectiveCurrentId]) return true;

      let prevMap = lastSavedParachuteSectionsRef.current[effectiveCurrentId];
      if (!prevMap) {
        prevMap = buildParachuteSectionSigMap(d);
      }
      const parachuteDirty = PARACHUTE_JSON_SECTION_ORDER.some(
        (section) => parachuteSectionSignature(d, section) !== (prevMap[section] ?? '')
      );
      if (parachuteDirty) return true;

      return false;
    },
    [effectiveCurrentId, data, analyses]
  );

  useEffect(() => {
    getArchivedAnalysesApi()
      .then((list) => {
        if (Array.isArray(list)) {
          setArchivedAnalyses(list);
        }
      })
      .catch(() => {});
  }, []);

  const setData = useCallback(
    (updater) => {
      setAnalyses((prev) =>
        prev.map((a) => {
          if (a.id !== effectiveCurrentId) return a;
          if (isAnalysisLocked(a.status)) return a;
          return { ...a, data: typeof updater === 'function' ? updater(a.data) : updater };
        })
      );
    },
    [effectiveCurrentId]
  );

  const createAnalysis = useCallback(
    (name?: string, companyLink?: { lawFirmId: string; companyId: string }) => {
      createAnalysisApi(name || '', companyLink)
        .then(async (newAnalysis) => {
          const list = await getAnalyses();
          const mapped =
            Array.isArray(list) && list.length > 0
              ? mapAnalysesFromApi(list)
              : [{ ...newAnalysis, data: normalizeAnalysisData(newAnalysis.data) }];
          setAnalyses(mapped);
          setCurrentAnalysisId(newAnalysis.id);
          setAnalysesRefreshVersion((v) => v + 1);
          navigate(`/client/${newAnalysis.id}`);
        })
        .catch((e) => {
          console.error('Create analysis failed:', e);
          alert('Could not create analysis. Check your connection and try again.');
        });
    },
    [navigate]
  );

  const copyAnalysis = useCallback(
    (sourceId, name, stepOptions) => {
      copyAnalysisApi(sourceId, name, stepOptions)
        .then(async (newAnalysis) => {
          const list = await getAnalyses();
          const mapped =
            Array.isArray(list) && list.length > 0
              ? mapAnalysesFromApi(list)
              : [{ ...newAnalysis, data: normalizeAnalysisData(newAnalysis.data) }];
          setAnalyses(mapped);
          setCurrentAnalysisId(newAnalysis.id);
          setAnalysesRefreshVersion((v) => v + 1);
          navigate(`/client/${newAnalysis.id}`);
        })
        .catch((e) => {
          console.error('Copy analysis failed:', e);
          alert('Could not copy analysis. Check your connection and try again.');
        });
    },
    [navigate]
  );

  const archiveAnalysis = useCallback(
    (id, reason) => {
      const a = analyses.find((x) => x.id === id);
      if (!a) return;
      const next = analyses.filter((x) => x.id !== id);
      setAnalyses(next);
      const archived = {
        ...a,
        deleteReason: reason || null,
        archivedAt: new Date().toISOString(),
      };
      setArchivedAnalyses((prev) => [...prev, archived]);
      if (currentAnalysisId === id) setCurrentAnalysisId(next[0]?.id ?? null);
    },
    [analyses, currentAnalysisId]
  );

  const deleteAnalysis = useCallback(
    (id, reason) => {
      if (analyses.length <= 1) return;
      const trimmedReason = (reason || '').trim();
      deleteAnalysisApi(id, trimmedReason || 'Archived from client')
        .then(() => Promise.all([getAnalyses(), getArchivedAnalysesApi()]))
        .then(([list, archived]) => {
          if (Array.isArray(list) && list.length > 0) {
            setAnalyses(mapAnalysesFromApi(list));
            if (!list.some((a) => a.id === currentAnalysisId)) {
              setCurrentAnalysisId(list[0].id);
            }
          } else {
            setAnalyses([]);
            setCurrentAnalysisId(null);
          }

          if (Array.isArray(archived)) {
            setArchivedAnalyses(archived);
          }
        })
        .catch(() => {
          // Fallback to local archive behavior if API fails
          archiveAnalysis(id, reason);
        });
    },
    [analyses.length, archiveAnalysis, currentAnalysisId]
  );

  const restoreAnalysis = useCallback(
    (id) => {
      restoreAnalysisApi(id)
        .then(() => Promise.all([getAnalyses(), getArchivedAnalysesApi()]))
        .then(([list, archived]) => {
          if (Array.isArray(list) && list.length > 0) {
            setAnalyses(mapAnalysesFromApi(list));
            setCurrentAnalysisId(id);
          }
          if (Array.isArray(archived)) {
            setArchivedAnalyses(archived);
          }
        })
        .catch(() => {
          // Fallback to local restore if API fails
          const a = archivedAnalyses.find((x) => x.id === id);
          if (!a) return;
          setArchivedAnalyses((prev) => prev.filter((x) => x.id !== id));
          setAnalyses((prev) => [...prev, a]);
          setCurrentAnalysisId(id);
        });
    },
    [archivedAnalyses]
  );

  const updateAnalysisName = useCallback((id, name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    updateAnalysisApi(id, trimmed)
      .then(() => getAnalyses())
      .then((list) => {
        if (list.length > 0) setAnalyses(mapAnalysesFromApi(list));
      })
      .catch(() => {
        setAnalyses((prev) => prev.map((a) => (a.id === id ? { ...a, name: trimmed } : a)));
      });
  }, []);

  useEffect(() => {
    if (
      data.companyName &&
      currentAnalysis?.name?.startsWith('Analysis ') &&
      !isAnalysisLocked(currentAnalysis?.status)
    ) {
      setAnalyses((prev) => prev.map((a) => (a.id === currentAnalysisId ? { ...a, name: data.companyName } : a)));
    }
  }, [data.companyName, currentAnalysis?.name, currentAnalysis?.status, currentAnalysisId]);

  const navigateTo = useCallback(
    (tab) => {
      const path = CLIENT_TAB_TO_PATH[tab] || '/client';
      const persistTabs = new Set([
        'transaction',
        'individuals',
        'compensation',
        'baseAmount',
        'parachute',
        'analysis',
        'documents',
      ]);
      const shouldPersist = persistTabs.has(tab);

      const persistAndNavigate = async () => {
        if (!shouldPersist || !effectiveCurrentId) {
          navigate(path);
          return;
        }
        await persistBasicsIfChanged();
        await persistIndividualsIfChanged();
        await persistCompensationIfChanged();
        await persistBaseAmountIfChanged();
        await persistSeveranceIfChanged();
        await persistParachuteJsonSectionsIfChanged();

        navigate(path);
      };

      persistAndNavigate().catch((e) => {
        // Keep user on the same step if persistence fails.
        console.error(e);
        alert('Could not save analysis changes. Please try again.');
      });
    },
    [
      navigate,
      effectiveCurrentId,
      persistBasicsIfChanged,
      persistIndividualsIfChanged,
      persistCompensationIfChanged,
      persistBaseAmountIfChanged,
      persistSeveranceIfChanged,
      persistParachuteJsonSectionsIfChanged,
      setParachuteSubTab,
      setParachuteEquitySubTab,
      setDocumentsSubTab,
    ]
  );

  const value = useMemo(
    () => ({
      analyses,
      setAnalyses,
      archivedAnalyses,
      currentAnalysisId,
      setCurrentAnalysisId,
      data,
      setData,
      createAnalysis,
      copyAnalysis,
      deleteAnalysis,
      archiveAnalysis,
      restoreAnalysis,
      updateAnalysisName,
      isCurrentAnalysisLocked,
      emptyAnalysis,
      navigateTo,
      persistBasicsIfChanged,
      persistIndividualsIfChanged,
      persistCompensationIfChanged,
      persistBaseAmountIfChanged,
      persistSeveranceIfChanged,
      persistParachuteJsonSectionsIfChanged,
      hasUnsavedWorkflowChanges,
      parachuteSubTab,
      setParachuteSubTab,
      parachuteEquitySubTab,
      setParachuteEquitySubTab,
      documentsSubTab,
      setDocumentsSubTab,
      currentAnalysis,
      pathToTab: CLIENT_PATH_TO_TAB,
      tabToPath: CLIENT_TAB_TO_PATH,
      refreshAnalysesList,
      analysesRefreshVersion,
      analysesListReady,
    }),
    [
      analyses,
      archivedAnalyses,
      currentAnalysisId,
      data,
      createAnalysis,
      copyAnalysis,
      deleteAnalysis,
      archiveAnalysis,
      restoreAnalysis,
      updateAnalysisName,
      isCurrentAnalysisLocked,
      navigateTo,
      persistBasicsIfChanged,
      persistIndividualsIfChanged,
      persistCompensationIfChanged,
      persistBaseAmountIfChanged,
      persistSeveranceIfChanged,
      persistParachuteJsonSectionsIfChanged,
      hasUnsavedWorkflowChanges,
      parachuteSubTab,
      parachuteEquitySubTab,
      documentsSubTab,
      currentAnalysis,
      refreshAnalysesList,
      analysesRefreshVersion,
      analysesListReady,
    ]
  );

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompanyContext(): CompanyContextValue {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompanyContext must be used within CompanyProvider');
  return ctx;
}

export type { CompanyContextValue } from './CompanyContextTypes';
